import React, { useEffect, useMemo, useRef, useState } from "react";
import { colors, radius, spacing, typography, cardBase } from "../../utils/designTokens";
import { useIsMobile } from "../../hooks/useResponsive";
import { useAdministracionesData } from "../../hooks/useAdministracionesData";
import { auth } from "../../firebase";
import { STATUS_LABELS } from "../../utils/statusColors";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import AppSelect from "../../components/AppSelect";
import AdministracionFicha from "../../components/AdministracionFicha";
import { estaBloqueado, estaLibre, fmtFechaLiberacion } from "../../utils/crmAsignacion";
import { FaBuilding, FaSearch, FaPlus, FaLock, FaMapMarkerAlt, FaStar } from "react-icons/fa";

const CRM_STATES = ["sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado"];
const CRM_OPTS = [{ value: "", label: "CRM: cualquiera" }, ...CRM_STATES.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))];
const ASIGN_OPTS = [
  { value: "todas", label: "Todas" },
  { value: "mias", label: "Mías" },
  { value: "libres", label: "Libres" },
];
const ORDEN_OPTS = [
  { value: "nombre", label: "Orden: nombre" },
  { value: "reviews", label: "Orden: más reseñas" },
  { value: "rating", label: "Orden: peor rating" },
];

/**
 * CRM de administraciones de edificios (comercial + superadmin). Igual que
 * Desarrolladoras: lista buscable con estado CRM y asignación (bloqueo 60 días),
 * más el alta de nuevas. La colección `administraciones` se precarga con un
 * relevamiento de Google Maps (rating/reseñas como señal de oportunidad).
 * `setSelected` llega a la ficha para el acceso "Cotizar Back office".
 */
export default function Administraciones({ setSelected }) {
  const isMobile = useIsMobile();
  const { administraciones, comerciales, loading, upsertLocal, reload } = useAdministracionesData();
  const emailLc = String(auth.currentUser?.email || "").toLowerCase();

  // La lista vive en un cache de módulo que no se refresca solo. Si al entrar ya
  // había datos en memoria (de otra visita), se releen sin tapar la lista, para
  // no abrir fichas con lo que otro agente o el Cotizador Back office cambió
  // mientras tanto. La primera carga ya la hace el hook.
  const refrescoRef = useRef({ conCache: !loading, reload });
  useEffect(() => {
    if (refrescoRef.current.conCache) refrescoRef.current.reload();
  }, []);

  const [busq, setBusq] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fAsign, setFAsign] = useState("todas");
  const [orden, setOrden] = useState("reviews");
  const [ficha, setFicha] = useState(undefined); // undefined = cerrada, null = nueva, obj = editar

  const filtradas = useMemo(() => {
    const q = busq.trim().toLowerCase();
    const arr = administraciones.filter((a) => {
      if (fEstado && (a.estado || "sin_contactar") !== fEstado) return false;
      if (fAsign === "mias" && String(a.asignadoEmail || "").toLowerCase() !== emailLc) return false;
      if (fAsign === "libres" && !estaLibre(a)) return false;
      if (q) {
        const hay = `${a.nombre || ""} ${a.email || ""} ${a.ciudadZona || ""} ${a.asignadoA || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    arr.sort((a, b) => {
      if (orden === "reviews") return (Number(b.nReviews) || 0) - (Number(a.nReviews) || 0);
      if (orden === "rating") return (Number(a.rating) || 99) - (Number(b.rating) || 99);
      return String(a.nombre || "").localeCompare(String(b.nombre || ""));
    });
    return arr;
  }, [administraciones, busq, fEstado, fAsign, orden, emailLc]);

  return (
    <div style={{ padding: isMobile ? 16 : spacing[6], maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr 1fr auto", gap: spacing[2], marginBottom: spacing[3] }}>
        <div style={{ position: "relative", gridColumn: isMobile ? "1 / -1" : "auto" }}>
          <FaSearch size={13} color={colors.gray400} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={busq} onChange={(e) => setBusq(e.target.value)} placeholder="Buscar por nombre, email, zona…" style={searchInput} aria-label="Buscar administración" />
        </div>
        {/* En mobile el filtro CRM se oculta: quedan 2 (asignación + orden) en
            una fila limpia. En desktop hay lugar para los tres. */}
        {!isMobile && <AppSelect value={fEstado} onChange={setFEstado} options={CRM_OPTS} ariaLabel="Estado CRM" />}
        <AppSelect value={fAsign} onChange={setFAsign} options={ASIGN_OPTS} ariaLabel="Asignación" />
        <AppSelect value={orden} onChange={setOrden} options={ORDEN_OPTS} ariaLabel="Orden" />
        <button onClick={() => setFicha(null)} style={{ ...addBtn, gridColumn: isMobile ? "1 / -1" : "auto" }}>
          <FaPlus size={12} /> Nueva administración
        </button>
      </div>

      {loading && administraciones.length === 0 ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando administraciones…</div>
      ) : filtradas.length === 0 ? (
        <EmptyState icon={FaBuilding} title="Sin administraciones" description="No hay resultados con estos filtros. Probá limpiar la búsqueda o cargá una nueva." />
      ) : (
        <div style={{ display: "grid", gap: spacing[2] }}>
          {filtradas.map((a) => (
            <AdmCard key={a.id} a={a} onClick={() => setFicha(a)} />
          ))}
        </div>
      )}

      <AdministracionFicha
        open={ficha !== undefined}
        onClose={() => setFicha(undefined)}
        admin={ficha || null}
        comerciales={comerciales}
        onSaved={(id, data) => upsertLocal(id, data)}
        setSelected={setSelected}
      />
    </div>
  );
}

function AdmCard({ a, onClick }) {
  const bloqueado = estaBloqueado(a);
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{ ...cardBase, padding: spacing[3], cursor: "pointer", display: "flex", alignItems: "center", gap: spacing[3] }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {a.nombre || "Sin nombre"}
        </div>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "flex", gap: spacing[2], flexWrap: "wrap", marginTop: 2 }}>
          {a.ciudadZona && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><FaMapMarkerAlt size={10} /> {a.ciudadZona}</span>}
          {a.rating != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: colors.warningDark }}><FaStar size={10} /> {a.rating}{a.nReviews != null ? ` (${a.nReviews})` : ""}</span>}
          {a.telefono && <span>· {a.telefono}</span>}
        </div>
        {a.asignadoA && (
          <div style={{ fontSize: typography.sizes.sm, marginTop: 3, color: bloqueado ? colors.primary : colors.gray600 }}>
            {bloqueado ? <><FaLock size={10} /> {a.asignadoA} · se libera {fmtFechaLiberacion(a)}</> : `${a.asignadoA} · libre (venció)`}
          </div>
        )}
      </div>
      <StatusBadge status={a.estado || "sin_contactar"} size="sm" />
    </div>
  );
}

const searchInput = {
  width: "100%", height: 42, padding: "0 12px 0 34px", boxSizing: "border-box",
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  fontSize: typography.sizes.base, fontFamily: typography.fontFamily, background: colors.white, color: colors.gray900,
};
const addBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, height: 42,
  padding: "0 16px", border: "none", borderRadius: radius.sm, background: colors.primary, color: colors.white,
  cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit", whiteSpace: "nowrap",
};
